import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { UsersService, WorkloadActivity } from '@horizon/core';
import {
  ConfirmationButtonsSetProperties,
  HorizonButtonComponent,
  HorizonConfirmationButtonsSetComponent,
} from '@horizon/core-buttons';
import { ToastService } from '@horizon/core-notifications';
import { TranslateService } from '@ngx-translate/core';
import { GenesysData } from '@shared/interfaces/CallLog';
import { PaymentNotification } from '@shared/interfaces/PaymentNotifications';
import { ApiExceptionsHandlerService } from '@shared/services/api-exceptions-handler.service';
import { HorizonGenesysCallLogService } from '@shared/services/horizon-genesys-call-log.service';
import { BeforeCloseEventArgs } from '@syncfusion/ej2-angular-popups';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { globalSettings } from 'src/global-settings';
import { ReportData } from '../../models';
import { FormsService } from '../../services/forms.service';

@Component({
  selector: 'call-log-report-view',
  templateUrl: './call-log-report-view.component.html',
  styleUrls: ['./call-log-report-view.component.scss'],
})
export class CallLogReportViewComponent implements OnInit, OnDestroy {
  @Input() genesysData: GenesysData;
  @Input() isEditMode = false;
  @Input() paymentNotification: PaymentNotification;
  @Input() paymentProcessed: boolean;
  @Input() paymentRequirementNotifications: Array<object>;
  @Input() reminders$: Observable<WorkloadActivity[]>;
  @Input() callType!: 'Inbound' | 'Outbound';

  @Output() cancelEvent = new EventEmitter<boolean>();
  @Output() openPCIPal = new EventEmitter<void>();
  @Output() saveReportData = new EventEmitter<ReportData>();

  @ViewChild('buttonsSet')
  confirmationButtons: HorizonConfirmationButtonsSetComponent;
  @ViewChild('CallClosureCodeButton')
  callClosureCodeButton: HorizonButtonComponent;
  @ViewChild('paymentButton') paymentButton: HorizonButtonComponent;

  //#region Private Properties
  private callId: string;
  private categories: Array<any>;
  private conversationDetails: {
    conversationId: string;
    participantId: string;
    communicationId: string;
  };
  private readonly destroy$ = new Subject<void>();
  private generalFormData: any;
  private identificationFieldsPassed: number = 0;
  private identificationFieldsVerified: Array<{
    name: string;
    value: 'Valid' | 'Invalid';
    from: 'Id' | 'Verification' | 'ThirdParty';
  }> = [];
  private idVPromptsData: any;
  public isCallSummaryEdited: boolean = false;
  public isCallSummaryPopulated: boolean = false;
  public isSuccessfulContact: boolean = false;
  public isIDVStatusValid: boolean = false;
  private passWordValidated: boolean = false;

  private previousStatusBeforeRefuse: string = 'Not Completed';
  private reportData: ReportData;
  private verificationFieldsPassed: number = 0;
  private verificationFieldsVerified: Array<{
    name: string;
    value: 'Valid' | 'Invalid';
    from: 'Id' | 'Verification' | 'ThirdParty';
  }> = [];
  private verificationSpecialFieldToVerify: string = 'Password';
  private wrapUpData: {
    state: string;
    code: string;
    name: string;
    provisional: boolean;
  } = {
    state: 'disconnected',
    code: '',
    name: '',
    provisional: false,
  };
  private readonly remindersToClose$ = new BehaviorSubject<number[]>([]);
  //#endregion

  //#region Public Properties
  public areFormsInitialized: boolean = false;
  public callClosureCode: string;
  public callClosureCodeSelected: boolean = false;
  public callClosureOptions = [];
  public callClosureOptionsValues = {
    text: 'caption',
    value: 'key',
  };
  public callDirection!: 'Inbound' | 'Outbound';
  public callSummary: string;
  public canUserEditCallSummary: boolean = false;
  public categorySelected: boolean = false;
  public categoryOptions = [];
  public categoryOptionsValues = {
    text: 'caption',
    value: 'key',
  };
  public currentLanguage: string;
  public customerRefusesToVerify: boolean = false;
  public generalForm: FormGroup;
  public generalFormIcon: { [key: string]: any } = {
    direction: '',
    id_v: '',
    vulnerability: 'None',
  };
  public readonly globalSettings: any = globalSettings;
  public identificationForm: FormGroup;
  public identificationFormGroupFields: Array<any>;

  public thirdPartyPanelForm: FormGroup;
  public thirdPartyPanelFormGroupFields: Array<any>;
  private thirdPartyPanelFieldsPassed: number = 0;
  private thirdPartyPanelFieldsVerified: Array<{
    name: string;
    value: 'Valid' | 'Invalid';
    from: 'Id' | 'Verification' | 'ThirdParty';
  }> = [];
  public isAgentCalling: boolean = false;
  public isTransferred: boolean = false;
  public isUnauthorised: boolean = false;
  public isThirdPartyPanel: boolean = false;
  public linkedAccounts: Array<any> = [];
  public prompts: Array<any>;
  public successfulContactOptions = [
    { caption: 'Yes', key: 'Yes' },
    { caption: 'No', key: 'No' },
  ];
  public successfulContactOptionsValues = {
    text: 'caption',
    value: 'key',
  };
  public telephoneNote: string;
  public verificationForm: FormGroup;
  public verificationFormGroupFields: Array<any>;
  public buttonCancelProps: ConfirmationButtonsSetProperties = {
    text: 'call_log.call_log_report_view.buttons.cancel',
    type: 'cancel',
  };
  public buttonConfirmProps: ConfirmationButtonsSetProperties = {
    text: 'call_log.call_log_report_view.buttons.save',
    type: 'confirm',
  };
  //#endregion

  constructor(
    private readonly apiExceptionsHandlerService: ApiExceptionsHandlerService,
    private readonly callLogService: HorizonGenesysCallLogService,
    private readonly cdr: ChangeDetectorRef,
    private readonly formBuilder: FormBuilder,
    private readonly formsService: FormsService,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService,
    private readonly userService: UsersService
  ) {}

  async ngOnInit(): Promise<void> {
    this.currentLanguage = this.translate.currentLang;
    await this.getFormData();
    await this.getReportData();
    if (!this.isEditMode) {
      this.canUserEditCallSummary = this.userService.checkUserPermission(
        'ACTIVITIES',
        'UPDATE'
      );
    } else {
      this.callId = this.genesysData.callID;
      this.callDirection = this.genesysData.callType;
      this.getIsUnauthorised();
      this.getIsTransferred();
      this.getIsThirdPartyPanel();
      this.getConversationDetails();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  //#region Public Methods
  /**
   * Updates the call closure code based on the selected event data
   *
   * @param {any} event - The event containing the selected call closure data
   */
  public callClosureChanged(event: any): void {
    if (event.itemData) {
      this.callClosureCodeSelected = true;
      this.reportData.callLog.callClosure = event.itemData.key;
      this.isCallSummaryPopulated = true;
    } else {
      this.callClosureCodeSelected = false;
      this.reportData.callLog.callClosure = '';
      this.isCallSummaryPopulated = false;
    }
    //* Old functionality from the call closure code verification
    // this.wrapUpData.code = event.itemData.key;
    // this.wrapUpData.name = event.itemData.caption;
  }

  /**
   * Updates category selection and refreshes call closure options
   *
   * @param {any} event - The event containing the selected category data
   */
  public categoryChanged(event: any): void {
    this.reportData.category = event.itemData.key;
    this.categorySelected = true;
    this.callClosureCodeSelected = false;
    this.callClosureOptions = this.populateCallClosureOptions(
      event.itemData.key
    );
  }

  /**
   * Emit cancelEvent when Cancel button is clicked
   */
  public close(args?: BeforeCloseEventArgs): void {
    if (document.activeElement.tagName === 'INPUT' && args) {
      args.cancel = true;
    } else {
      this.cancelEvent.emit(true);
    }
  }

  /**
   * Retrieves a form control from a form group
   *
   * @param {any} formGroup - The form group containing the control
   * @param {string} fieldName - The name of the control to retrieve
   * @returns {AbstractControl} The requested form control
   */
  public getControl(formGroup: any, fieldName: string): AbstractControl {
    if (formGroup && formGroup instanceof FormGroup) {
      return formGroup.get(`${fieldName}`);
    }
    return new FormControl('');
  }

  public openPCIPalAction(): void {
    this.openPCIPal.emit();
  }

  /**
   * Handles populating call summary data and updates UI accordingly
   */
  public populateCallSummary(): void {
    this.callLogService
      .setCallClosureCode(this.conversationDetails, this.wrapUpData)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => this.handleError(error, 'Genesys API')),
        finalize(() => this.callClosureCodeButton.stopLoading())
      )
      .subscribe({
        complete: () => {
          this.toastService.showToastNotification({
            text: 'call_log.success.set_call_closure',
            type: 'success',
          });
          this.isCallSummaryPopulated = true;
        },
      });
  }

  public refuseToCompleteIDVChanged(): void {
    this.customerRefusesToVerify = !this.customerRefusesToVerify;
    if (!this.customerRefusesToVerify) {
      this.generalForm.controls['id_v'].setValue(
        this.previousStatusBeforeRefuse
      );
      this.reportData.callLog.idVStatus = this.previousStatusBeforeRefuse;
    } else {
      this.previousStatusBeforeRefuse = this.generalForm.controls['id_v'].value;
      this.generalForm.controls['id_v'].setValue('Refused');
      this.reportData.callLog.idVStatus = 'Refused';
    }
  }

  /**
   * Saves the current report data
   */
  public saveData(): void {
    if (this.canUserEditCallSummary && !this.isEditMode) {
      this.reportData.callLog.id = this.generalFormData.rows[0].id.value;
    }
    if (this.isUnauthorised) this.reportData.callLog.idVStatus = 'NA';

    if (this.isThirdPartyPanel)
      this.reportData.callLog.additionalFields = {
        ...this.reportData.callLog.additionalFields,
        IsThirdPartyPanel: true,
      };

    if (this.customerRefusesToVerify)
      this.reportData.callLog.idVStatus = 'Refused';
    this.reportData.activityText =
      this.reportData.callLog.callSummaryEdited.length > 0
        ? this.reportData.callLog.callSummaryEdited
        : this.reportData.callLog.telephoneNote;

    this.reportData.tasksToClose = this.remindersToClose$.value;
    this.reportData.entityType = 'Account';
    this.saveReportData.emit(this.reportData);
  }

  /**
   * Toggles the selection state of a linked account
   *
   * @param {{ accountId: number; accountRef: string }} accountId - The account information
   */
  public selectLinkedAccount(accountId: {
    accountId: number;
    accountRef: string;
  }): void {
    const clickedLinkedAccount =
      this.reportData.callLog.additionalFields.LinkedAccounts.findIndex(
        (linkedAccount) => linkedAccount.AccountId === accountId.accountId
      );
    if (clickedLinkedAccount !== -1)
      this.reportData.callLog.additionalFields.LinkedAccounts[
        clickedLinkedAccount
      ].Selected =
        !this.reportData.callLog.additionalFields.LinkedAccounts[
          clickedLinkedAccount
        ].Selected;
  }

  /**
   * Updates the successful contact status based on the event data
   *
   * @param {any} event - The event containing the selected contact status
   */
  public successfulContactChanged(event: any): void {
    this.isSuccessfulContact = event.itemData.key === 'Yes' ? true : false;
    this.reportData.callLog.successfulContact = this.isSuccessfulContact;
  }

  /**
   * The function `switchChanged` updates the `Result` property of a specific
   * content item in `reportData.callLog.Content` based on the event and origin
   * form, and performs form verifications accordingly.
   * @param {any} event - The `event` parameter in the `switchChanged` function
   * seems to be an object that contains information about the event that triggered
   * the switch change. It likely includes properties like `name` and `value` that
   * are used to identify the specific switch that was changed and the new value of
   * the switch.
   * @param {string} originForm - The `originForm` parameter in the `switchChanged`
   * function represents the form type from which the event originated. It is used
   * to determine which specific form verification function to call based on the
   * type of form that triggered the event.
   */
  public switchChanged(event: any, originForm: string): void {
    const contentToModify = this.reportData.callLog.content.findIndex(
      (content) =>
        content.type.toLowerCase() === originForm.toLowerCase() &&
        content.textId === event.name
    );
    if (this.isEditMode)
      this.reportData.callLog.content[contentToModify].result = event.value;

    const newFieldToVerify = {
      name: event.name,
      value: event.value,
      from: originForm,
    };
    this.idAndVFormsVerification(newFieldToVerify);

    if (
      this.thirdPartyPanelFieldsVerified.length >= 1 ||
      (this.identificationFieldsVerified.length >= 2 &&
        (this.verificationFieldsVerified.length >= 2 || this.passWordValidated))
    ) {
      this.generalForm.controls['id_v'].setValue(
        this.thirdPartyPanelFieldsPassed >= 1 ||
          (this.identificationFieldsPassed >= 2 &&
            (this.verificationFieldsPassed >= 2 || this.passWordValidated))
          ? 'Passed'
          : 'Failed'
      );
      this.reportData.callLog.idVStatus =
        this.generalForm.controls['id_v'].value;
      this.isIDVStatusValid =
        this.generalForm.controls['id_v'].value === 'Passed';
    }
    this.setGeneralFormId_VIcon();
  }

  /**
   * Updates form data based on text area changes
   *
   * @param {string} field - The field being updated
   * @param {any} event - The change event data
   */
  public textAreaChange(field: string, event: any): void {
    if (this.isEditMode) {
      if (field === 'TelephoneNote')
        this.reportData.callLog.telephoneNote = event;
      if (field === 'CallSummaryEdited')
        this.reportData.callLog.callSummaryEdited = event;
    } else if (field === 'CallSummaryEdited') {
      this.reportData.callLog.callSummaryEdited = event;
      if (this.generalFormData.rows[0].callSummaryEdited.value !== event)
        this.isCallSummaryEdited = true;
    }
  }

  public onReminderSelected(data: WorkloadActivity[]): void {
    this.remindersToClose$.next(data.map((a) => a.id));
  }

  //#endregion
  //#region Private Methods
  /**
   * Creates a form group based on filtered rows data
   *
   * @param {any[]} rows - Array of form field data
   * @param {string} type - Type of form to build
   * @returns {FormGroup} The constructed form group
   */
  private buildForm(rows: any[], type: string): FormGroup {
    const group: { [key: string]: FormControl } = {};

    rows
      .filter(
        (row) =>
          row.type.value.toLowerCase() === type.toLowerCase() &&
          (row.answer?.value ?? '') !== ''
      )
      .forEach((row) => {
        group[row.textId.value] = new FormControl(
          row.fieldType.value === 'Date'
            ? new Date(row.answer.value)
            : row.answer.value || ''
        );
      });

    return this.formBuilder.group(group);
  }

  /**
   * Creates dynamic forms for identification and verification
   *
   * @returns {Observable<void>}
   */
  private generateDynamicForm(): Observable<void> {
    return of(this.idVPromptsData['rows']).pipe(
      map((rows: any[]) => {
        this.identificationForm = this.buildForm(rows, 'Id');
        this.verificationForm = this.buildForm(rows, 'Verification');
        this.thirdPartyPanelForm = this.buildForm(rows, 'ThirdParty');
      })
    );
  }

  /**
   * Retrieves conversation details from the service
   */
  private getConversationDetails(): void {
    this.callLogService
      .getConversationDetails(this.callId)
      .pipe(
        filter((conversationDetails) => !!conversationDetails),
        map((conversationDetails) => {
          return {
            conversationId: conversationDetails.conversationId,
            participantId: conversationDetails.participants[0].participantId,
            communicationId:
              conversationDetails.participants[0].sessions[0].sessionId,
          };
        }),
        tap((conversationDetailsMapped) => {
          this.conversationDetails = conversationDetailsMapped;
          this.handleSuccess('call_log.success.found_data');
        }),
        catchError((error) => this.handleError(error, 'Genesys Integration'))
      )
      .subscribe();
  }

  /**
   * Retrieves and processes form data
   */
  private async getFormData(): Promise<void> {
    this.callLogService.formData$
      .pipe(
        filter((_formData) => _formData !== null),
        first(),
        takeUntil(this.destroy$),
        tap((_formData) => {
          this.generalFormData = _formData.General.data;
          this.idVPromptsData = _formData.ID_V_Prompt.data;
          this.initializeForms();
        }),
        catchError((error) => this.handleError(error, 'Genesys Integration'))
      )
      .subscribe();
  }

  private getIsUnauthorised(): void {
    this.callLogService.isUnauthorised$
      .pipe(
        filter((res) => !!res),
        first(),
        takeUntil(this.destroy$),
        tap((res) => {
          this.isUnauthorised = res;
          if (this.isUnauthorised) {
            this.setIsUnauthorised();
          }
        }),
        catchError((error) => {
          this.apiExceptionsHandlerService.defaultErrorToasts(
            error,
            'Call Log'
          );
          return EMPTY;
        })
      )
      .subscribe();
  }

  private setIsUnauthorised(): void {
    this.generalForm.controls['id_v'].setValue('Unauthorised');
    this.generalForm.controls['callFromTo'].setValue('Unauthorised Caller');
    this.reportData.callLog.idVStatus = 'NA';
  }

  private getIsTransferred(): void {
    this.callLogService.isTransferred$
      .pipe(
        filter((res) => !!res),
        first(),
        takeUntil(this.destroy$),
        tap((res) => {
          this.isTransferred = res;
          if (this.isTransferred) {
            this.setIsTransferred();
          }
        }),
        catchError((error) => {
          this.apiExceptionsHandlerService.defaultErrorToasts(
            error,
            'Call Log'
          );
          return EMPTY;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private setIsTransferred(): void {
    this.generalForm.controls['id_v'].setValue('Passed');
    this.reportData.callLog.idVStatus = 'Passed';
    this.isIDVStatusValid = true;
  }

  private getIsThirdPartyPanel(): void {
    this.callLogService.isThirdPartyPanel$
      .pipe(
        takeUntil(this.destroy$),
        filter((res) => res),
        tap(() => (this.isThirdPartyPanel = true)),
        catchError((error) =>
          this.apiExceptionsHandlerService.catchErrorHandler(error, 'Call Log')
        )
      )
      .subscribe();
  }

  /**
   * Retrieves report data from the service
   */
  private getReportData(): void {
    this.callLogService.reportData$
      .pipe(
        filter((reportData) => !!reportData),
        takeUntil(this.destroy$),
        tap((reportData) => {
          this.reportData = reportData;
          this.handleSuccess('call_log.success.found_data');
        }),
        catchError((error) => this.handleError(error, 'Genesys Integration'))
      )
      .subscribe();
  }

  /**
   * Handles verification of identification and verification form fields
   *
   * @param {any} newFieldToVerify - Field to be verified
   */
  private idAndVFormsVerification(newFieldToVerify: any): void {
    let groupFieldToVerify;
    switch (newFieldToVerify.from) {
      case 'Id':
        groupFieldToVerify = 'identificationFieldsVerified';
        break;
      case 'Verification':
        groupFieldToVerify = 'verificationFieldsVerified';
        break;
      case 'ThirdParty':
        groupFieldToVerify = 'thirdPartyPanelFieldsVerified';
        break;
    }

    const index = this[groupFieldToVerify].findIndex(
      (field) =>
        field.name === newFieldToVerify.name &&
        field.from === newFieldToVerify.from
    );

    let fieldsPassed;
    switch (newFieldToVerify.from) {
      case 'Id':
        fieldsPassed = 'identificationFieldsPassed';
        break;
      case 'Verification':
        fieldsPassed = 'verificationFieldsPassed';
        break;
      case 'ThirdParty':
        fieldsPassed = 'thirdPartyPanelFieldsPassed';
        break;
    }

    if (index !== -1) {
      const pastValue = this[groupFieldToVerify][index].value;
      this[groupFieldToVerify][index].value = newFieldToVerify.value;

      if (newFieldToVerify.value === 'Valid' && pastValue !== 'Valid') {
        this[fieldsPassed] += 1;
      } else if (
        newFieldToVerify.value === 'Invalid' &&
        pastValue === 'Valid' &&
        this[fieldsPassed] > 0
      ) {
        this[fieldsPassed] -= 1;
      }
    } else {
      this[groupFieldToVerify].push(newFieldToVerify);
      if (newFieldToVerify.value === 'Valid') {
        this[fieldsPassed] += 1;
      }
    }

    this.verificationFieldsVerified.find((verifiedField) => {
      if (
        verifiedField.name.toLowerCase() ===
        this.verificationSpecialFieldToVerify.toLowerCase()
      ) {
        this.passWordValidated = verifiedField.value === 'Valid';
      }
    });
  }

  /**
   * Initializes all forms in the component
   */
  private async initializeForms(): Promise<void> {
    this.initializeGeneralForm();
    this.identificationFormGroupFields = this.mapRowToFormData(
      this.idVPromptsData.rows,
      'Id',
      true
    );
    this.verificationFormGroupFields = this.mapRowToFormData(
      this.idVPromptsData.rows,
      'Verification',
      true
    );
    this.thirdPartyPanelFormGroupFields = this.mapRowToFormData(
      this.idVPromptsData.rows,
      'ThirdParty',
      true
    );

    this.prompts = this.mapRowToFormData(this.idVPromptsData.rows, 'Prompt');

    await this.generateDynamicForm()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.cdr.detectChanges(),
        error: (error) => {
          this.toastService.showToastNotification({
            text: 'call_log.errors.initialized_call_log',
            type: 'error',
          });
        },
        complete: () => {
          this.toastService.showToastNotification({
            text: 'call_log.success.initialized_call_log',
            type: 'success',
          });
        },
      });

    if (!this.isEditMode) {
      this.generalForm.disable({ onlySelf: true });
      this.identificationForm.disable({ onlySelf: true });
      this.verificationForm.disable({ onlySelf: true });
      this.thirdPartyPanelForm.disable({ onlySelf: true });
      this.telephoneNote = this.generalFormData.rows[0].callLogNote?.value;
      this.callSummary = this.generalFormData.rows[0].callSummaryEdited?.value;
    }
    this.cdr.detectChanges();
    this.areFormsInitialized = true;
  }

  /**
   * Initializes the general form with default values
   */
  private initializeGeneralForm(): void {
    this.populateCategoryOptions();
    if (this.isEditMode) {
      this.isAgentCalling = this.genesysData.callSubType === 'Agent';
    } else {
      const status = this.generalFormData.rows[0].idvStatus?.value;
      (this.controlGeneralFlags[status] || this.controlGeneralFlags.default)();
      this.isThirdPartyPanel =
        this.generalFormData.rows[0].isThirdPartyPanel?.value ?? false;
    }
    const result = this.formsService.createGeneralForm({
      generalFormData: this.generalFormData.rows[0],
      isAgentCalling: this.isAgentCalling,
      isUnauthorised: this.isUnauthorised,
      isTransferred: this.isTransferred,
    });
    this.generalForm = result;
    this.linkedAccounts =
      this.generalFormData.rows[0].linkedAccounts?.value || null;
    this.isSuccessfulContact =
      this.generalFormData.rows[0].successfulContact.value;
    this.setGeneralFormIcons();
  }

  private controlGeneralFlags = {
    Refused: () => (this.customerRefusesToVerify = true),
    NA: () => (this.isUnauthorised = true),
    default: () => {
      if (this.generalFormData.rows[0].callClosure.value === 'callTransferred')
        this.isTransferred = true;
    },
  };

  /**
   * Maps row data to form fields based on form type
   *
   * @param {any} rows - The rows data to map
   * @param {string} formType - The type of form to map to
   * @param {boolean} [excludeEmptyAnswers=false] - Whether to exclude rows with empty/undefined answers
   * @returns {Array<any>} Mapped form data
   */
  private mapRowToFormData(
    rows: any,
    formType: string,
    excludeEmptyAnswers: boolean = false
  ): Array<any> {
    return rows.filter((row: any) => {
      const typeMatches =
        row.type.value.toLowerCase() === formType.toLowerCase() &&
        !!row.answer.value;
      if (!excludeEmptyAnswers) return typeMatches;

      return (
        typeMatches &&
        row.answer?.value !== undefined &&
        row.answer?.value !== ''
      );
    });
  }

  /**
   * Populates call closure options based on category
   *
   * @param {string} categoryName - The selected category name
   * @returns {Array<any>} Array of call closure options
   */
  private populateCallClosureOptions(categoryName: string): Array<any> {
    if (categoryName !== '' && this.categories && this.categories.length > 0) {
      const filteredCategory = this.categories.find(
        (filteredCategory) => filteredCategory.name === categoryName
      );
      return filteredCategory.children.map((category) => ({
        key: category.name,
        caption: category.displayName[this.currentLanguage.toLowerCase()],
      }));
    }
    return [];
  }

  /**
   * Populates category options from form data
   */
  private populateCategoryOptions(): void {
    this.categories = this.generalFormData.columns.find(
      (column) => column.name === 'Category'
    ).options;
    this.callClosureOptions = this.populateCallClosureOptions(
      this.generalFormData.rows[0].category.value
    );
    this.categoryOptions = this.categories.map((category) => ({
      key: category.name,
      caption: category.displayName[this.currentLanguage.toLowerCase()],
    }));
  }

  /**
   * Sets form icons based on current form values
   */
  private setGeneralFormIcons(): void {
    this.generalFormIcon.direction = {
      iconName:
        this.generalForm.controls['direction'].value === 'Outbound'
          ? 'callOut'
          : 'callIn',
      iconGroup: '',
      iconColor: globalSettings.colors.mainColor,
    };
    this.setGeneralFormId_VIcon();
    this.generalFormIcon.vulnerability = {
      iconName:
        this.generalForm.controls['vulnerability'].value === 'Low'
          ? 'tick_rounded'
          : this.generalForm.controls['vulnerability'].value === 'Medium'
          ? 'warning'
          : this.generalForm.controls['vulnerability'].value === 'High'
          ? 'cross_rounded'
          : 'warning',
      iconGroup:
        this.generalForm.controls['vulnerability'].value === 'Low'
          ? 'tooltip'
          : this.generalForm.controls['vulnerability'].value === 'Medium'
          ? ''
          : this.generalForm.controls['vulnerability'].value === 'High'
          ? ''
          : 'tooltip',
      iconColor:
        this.generalForm.controls['vulnerability'].value === 'Low'
          ? globalSettings.colors.successColor
          : this.generalForm.controls['vulnerability'].value === 'Medium'
          ? globalSettings.colors.warningColor
          : this.generalForm.controls['vulnerability'].value === 'High'
          ? globalSettings.colors.errorColor
          : globalSettings.colors.neutralDarkColor,
    };
  }

  /**
   * Sets the ID verification icon state
   */
  private setGeneralFormId_VIcon(): void {
    this.generalFormIcon.id_v = {
      iconName:
        this.generalForm.controls['id_v'].value === 'Not Completed'
          ? 'warning'
          : this.generalForm.controls['id_v'].value === 'Passed'
          ? 'tick_rounded'
          : 'cross_rounded',
      iconGroup:
        this.generalForm.controls['id_v'].value === 'Not Completed'
          ? 'tooltip'
          : '',
      iconColor:
        this.generalForm.controls['id_v'].value === 'Not Completed'
          ? globalSettings.colors.neutralDarkColor
          : this.generalForm.controls['id_v'].value === 'Passed'
          ? globalSettings.colors.successColor
          : globalSettings.colors.errorColor,
    };
  }

  private handleError(error: any, apiName: string): Observable<never> {
    console.error(error);
    this.apiExceptionsHandlerService.defaultErrorToasts(error, apiName);
    return EMPTY;
  }

  private handleSuccess(message: string): void {
    this.toastService.showToastNotification({
      text: message,
      type: 'success',
    });
  }
  //#endregion
}
